import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const cspUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Computer Systems and Networks",
  examWeight: "11–15%",
  focus: "Understanding the physical and logical infrastructure of the global Internet, data standardization, fault tolerance, and parallel/distributed computing models.",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: The Internet",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 1. Fundamental Definitions</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Computing Device" 
                definition="A physical artifact that can run a program. Examples: Computers, tablets, servers, routers, smart sensors, smart fridges."
              />
              <KeyTerm 
                term="Computing System" 
                definition="A group of computing devices and programs working together for a common purpose (e.g., a laptop connected to a printer and a Wi-Fi router)."
              />
              <KeyTerm 
                term="Computer Network" 
                definition="A group of interconnected computing devices capable of sending or receiving data. A computer network is a specific type of computing system."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ 2. Structure of the Internet</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Internet is a <strong>network of networks</strong> that connects devices globally using standardized, open protocols. It is a <strong>distributed system</strong> with no single central control point.
            </p>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🛣️ Path</h4>
                <p className="text-sm text-slate-700 mt-1">A sequence of directly connected computing devices that begins at the sender and ends at the receiver.</p>
              </div>
              <div className="bg-gradient-to-br from-indigo-100 to-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">🚦 Routing</h4>
                <p className="text-sm text-slate-700 mt-1">The process of finding a path. Internet routing is <strong>Dynamic</strong>—routers choose paths based on traffic, broken lines, or congestion.</p>
              </div>
            </div>
            <ImportantNote>
              <strong>AP Exam Note:</strong> Data sent from device A to device B does not always take the same path, even if sent consecutively.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📶 3. Data Transfer and Bandwidth</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">📦 Data Stream</h4>
                <p className="text-sm text-slate-700 mt-1">Information passed through the Internet as chunks of data encapsulated into <strong>packets</strong>.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                  <h4 className="font-bold text-cyan-900">🌊 Bandwidth</h4>
                  <p className="text-xs text-slate-700 mt-1">The maximum amount of data that can be sent in a fixed time (bps, Mbps, Gbps).</p>
                  <p className="text-xs font-semibold text-cyan-800 mt-2 italic">Analogy: The width of a pipe (capacity).</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-blue-900">⏱️ Latency</h4>
                  <p className="text-xs text-slate-700 mt-1">The time it takes for a single bit to travel from sender to receiver.</p>
                  <p className="text-xs font-semibold text-blue-800 mt-2 italic">Analogy: The length of the pipe (delay).</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📜 4. Protocols (Rules of the Road)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Protocols are an agreed-upon set of rules. They must be <strong>Open (Nonproprietary)</strong> so different devices (iOS vs Windows) can communicate. Managed by the <strong>IETF</strong> (Internet Engineering Task Force).
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">📍 IP (Internet Protocol)</h4>
                <p className="text-sm text-slate-700 mt-1">Uniquely identifies devices. <strong>IPv4</strong> uses 32 bits (~4 billion addresses); <strong>IPv6</strong> uses 128 bits (340 undecillion addresses) to solve scarcity.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🤝 TCP (Transmission Control Protocol)</h4>
                <p className="text-sm text-slate-700 mt-1">Ensures <strong>Reliable</strong> delivery. Numbers packets, requests resending of lost data, and reassembles them in order. Used for Email and Web browsing.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">⚡ UDP (User Datagram Protocol)</h4>
                <p className="text-sm text-slate-700 mt-1">Ensures <strong>Fast</strong> delivery. No error checking or reordering. Used for live video, gaming, and VoIP.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🔗 HTTP & HTTPS</h4>
                <p className="text-sm text-slate-700 mt-1">Transmits web pages. Browser sends <code>GET</code> requests. <strong>HTTPS</strong> adds encryption (TLS/SSL) for security.</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-50 to-sky-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-cyan-900">📖 DNS (Domain Name System)</h4>
                <p className="text-sm text-slate-700 mt-1">Hierarchical system that translates human-readable names (google.com) to machine-readable IP addresses.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🖥️ Web vs. Internet</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-slate-100 p-3 rounded-lg border border-slate-300">
                <p className="text-xs font-bold text-slate-900">The Internet</p>
                <p className="text-xs text-slate-600">The physical network hardware, cables, and IP protocols.</p>
              </div>
              <div className="bg-slate-100 p-3 rounded-lg border border-slate-300">
                <p className="text-xs font-bold text-slate-900">The WWW</p>
                <p className="text-xs text-slate-600">An application of linked pages running <em>on top</em> of the Internet using HTTP.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Fault Tolerance",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ Reliability and Redundancy</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Components fail constantly. The Internet is engineered to be <strong>Fault Tolerant</strong>—the ability to continue functioning despite component failures.
            </p>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-bold text-amber-900">🔗 Redundancy</h4>
              <p className="text-sm text-slate-700 mt-1">The inclusion of extra components (extra cables, routers, paths). Redundant routing ensures that if one line is cut, dynamic protocols automatically reroute traffic.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📦 Packet Switching</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Data is broken into packets to maximize efficiency and reliability.</p>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Anatomy of a Packet</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                  <li><strong>Payload:</strong> The actual data chunk.</li>
                  <li><strong>Metadata:</strong> Data <em>about</em> the data (Destination IP, Source IP, Sequence Number).</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                <h4 className="font-bold text-blue-900 mb-2 text-sm">The Process:</h4>
                <ol className="list-decimal pl-5 text-xs text-slate-700 space-y-2">
                  <li>Files are chopped into packets.</li>
                  <li>Each packet is sent individually and may take <strong>different paths</strong>.</li>
                  <li>Packets may arrive <strong>out of order</strong>.</li>
                  <li>The receiver uses sequence numbers in metadata to reassemble them.</li>
                </ol>
                <p className="mt-3 text-xs font-bold text-green-700">Benefit: If a line fails, only lost packets are resent, not the whole file.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Scalability</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Scalability:</strong> The capacity for the system to change in size and scale to meet new demands without needing to be rewritten.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Parallel and Distributed Computing",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ Computing Models</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">1. Sequential Computing</h4>
                <p className="text-sm text-slate-700">Operations performed in order, one at a time. Slower as data grows.</p>
                <div className="mt-2 text-xs font-mono bg-white p-2 border inline-block">
                  <InlineMath math="Time = \text{Sum of all steps}" />
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">2. Parallel Computing</h4>
                <p className="text-sm text-slate-700">Program is broken into pieces; some run simultaneously on multi-core hardware.</p>
                <div className="mt-2 text-xs font-mono bg-white p-2 border inline-block">
                  <InlineMath math="Time = \text{Sequential Part} + \text{Longest Parallel Part}" />
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Distributed Computing</h4>
                <p className="text-sm text-slate-700">Uses multiple devices across a network to run a program. Used for massive data sets like Google Search indexing or protein folding.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Calculating Speedup</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="\text{Speedup} = \frac{\text{Time taken by Sequential Solution}}{\text{Time taken by Parallel Solution}}" />
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-800 text-sm mb-2">Example Calculation:</h4>
              <p className="text-xs text-slate-600 mb-2">Task: 1 sequential step (10s) and 3 independent steps (20s each).</p>
              <ul className="text-xs text-slate-700 space-y-1 ml-4 list-disc">
                <li><strong>Sequential Time:</strong> <InlineMath math="10 + 20 + 20 + 20 = 70 \text{s}" /></li>
                <li><strong>Parallel Time (3 processors):</strong> <InlineMath math="10 + 20 = 30 \text{s}" /> (Parallel steps run at once)</li>
                <li><strong>Speedup:</strong> <InlineMath math="70 / 30 = 2.33" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Efficiency Limits (Amdahl's Argument)</h3>
            <ImportantNote>
              Adding more processors does not infinitely speed up a process because of the <strong>Sequential Portion</strong>. Overhead (coordination between processors) eventually outweighs the benefit of adding more.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✅ Exam Summary Checklist</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <ul className="grid md:grid-cols-2 gap-3">
                <li className="flex gap-2 text-xs text-green-800"><span>✔️</span> Redundancy creates Fault Tolerance</li>
                <li className="flex gap-2 text-xs text-green-800"><span>✔️</span> Scalability = growth without breaking</li>
                <li className="flex gap-2 text-xs text-green-800"><span>✔️</span> Protocols MUST be open</li>
                <li className="flex gap-2 text-xs text-green-800"><span>✔️</span> Packet Switching uses metadata</li>
                <li className="flex gap-2 text-xs text-green-800"><span>✔️</span> TCP = Reliable; UDP = Fast</li>
                <li className="flex gap-2 text-xs text-green-800"><span>✔️</span> Web runs on top of the Internet</li>
                <li className="flex gap-2 text-xs text-green-800"><span>✔️</span> Parallel is limited by sequential tasks</li>
              </ul>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default cspUnit4Notes;